import * as THREE from 'three/webgpu';

import { expandImportedModelRenderableTargets, collectImportedModelRenderableMeshTargets } from '../../imported-model/highlight/ImportedAssemblyPartTargetResolver';
import {
    ensureImportedModelTopologyPickHost,
} from '../../imported-model/topology/imported-model-topology-runtime';
import {
    ImportedModelTopologyRepository,
    resolveImportedModelTopologyRepository,
} from '../../imported-model/topology/ImportedModelTopologyRepository';
import type { ImportedMeshTopologyBinding } from '../../imported-model/topology/ImportedMeshTopologyBinding';
import {
    FeatureResultMesh,
    isFeatureResultMesh,
} from '../../mesh/feature/FeatureResultMesh';
import type { Viewer } from '../../viewer/Viewer';
import type { MeasureEntityRef } from '../types/measure-types';
import type { MeasureTreePartSelectionDescriptor } from '../types/measure-tree-part-selection-types';
import { resolveMeasureEntityRefFromProgrammaticPick } from './MeasureSelectionBootstrap';

/**
 * 导入 mesh primitive 面范围项。
 */
interface ImportedMeshPrimitiveFaceRangeItem {
    /** glTF primitive 稳定 ID。 */
    primitiveId: string;
}

/**
 * 零件子树可测量几何展开器。
 *
 * 职责：将设计树零件/实体节点解析为其管辖范围内全部 BRep 可测量拓扑引用。
 */
export class MeasurePartMeasurableGeometryExpander {
    /**
     * 展开设计树零件节点下的全部可测量几何。
     *
     * @param viewer Viewer 实例。
     * @param descriptor 设计树节点测量展开描述符。
     * @returns 去重后的测量实体引用列表。
     */
    public expand(
        viewer: Viewer,
        descriptor: MeasureTreePartSelectionDescriptor,
    ): MeasureEntityRef[] {
        const pickHosts = this.resolvePickHosts(viewer, descriptor);
        const entities: MeasureEntityRef[] = [];
        const seenObjectIds = new Set<string>();

        pickHosts.forEach((pickHost) => {
            this.expandPickHostMeasurableEntities(pickHost).forEach((entityRef) => {
                if (seenObjectIds.has(entityRef.objectId)) {
                    return;
                }
                seenObjectIds.add(entityRef.objectId);
                entities.push(entityRef);
            });
        });

        return entities;
    }

    /**
     * 解析描述符对应的全部拓扑 pick host。
     *
     * @param viewer Viewer 实例。
     * @param descriptor 设计树节点测量展开描述符。
     * @returns pick host 列表。
     */
    private resolvePickHosts(
        viewer: Viewer,
        descriptor: MeasureTreePartSelectionDescriptor,
    ): FeatureResultMesh[] {
        if (Array.isArray(descriptor.highlightTargets) && descriptor.highlightTargets.length > 0) {
            return this.resolvePickHostsFromHighlightTargets(viewer, descriptor);
        }

        const objectId = descriptor.objectId?.trim() ?? '';
        if (!objectId) {
            return [];
        }

        const sceneObject = viewer.findSceneObjectById(objectId);
        if (!sceneObject) {
            return [];
        }

        if (isFeatureResultMesh(sceneObject) && sceneObject.getTopologySnapshot()) {
            return [sceneObject];
        }

        return this.resolvePickHostsFromSceneRoot(viewer, sceneObject);
    }

    /**
     * 从 highlightTargets 解析 pick host。
     *
     * @param viewer Viewer 实例。
     * @param descriptor 设计树节点测量展开描述符。
     * @returns pick host 列表。
     */
    private resolvePickHostsFromHighlightTargets(
        viewer: Viewer,
        descriptor: MeasureTreePartSelectionDescriptor,
    ): FeatureResultMesh[] {
        const pickHosts: FeatureResultMesh[] = [];
        const seenHostIds = new Set<string>();
        const searchRoot = this.resolveHighlightTargetSearchRoot(viewer, descriptor);

        descriptor.highlightTargets?.forEach((highlightTarget) => {
            const matchedObject = this.findObjectByIdInSubtree(
                searchRoot,
                highlightTarget.objectId,
            ) ?? viewer.findSceneObjectById(highlightTarget.objectId);
            if (!matchedObject) {
                return;
            }

            expandImportedModelRenderableTargets(
                matchedObject,
                this.normalizePrimitiveIndices(highlightTarget.primitiveIndices),
            ).forEach((renderableTarget) => {
                this.collectPickHostsFromRenderableTarget(
                    renderableTarget.object,
                    renderableTarget.primitiveIndices,
                    pickHosts,
                    seenHostIds,
                );
            });
        });

        return pickHosts;
    }

    /**
     * 从场景根对象解析其下全部 pick host。
     *
     * @param viewer Viewer 实例。
     * @param sceneRoot 场景根对象。
     * @returns pick host 列表。
     */
    private resolvePickHostsFromSceneRoot(
        _viewer: Viewer,
        sceneRoot: THREE.Object3D,
    ): FeatureResultMesh[] {
        const pickHosts: FeatureResultMesh[] = [];
        const seenHostIds = new Set<string>();

        expandImportedModelRenderableTargets(sceneRoot).forEach((renderableTarget) => {
            this.collectPickHostsFromRenderableTarget(
                renderableTarget.object,
                renderableTarget.primitiveIndices,
                pickHosts,
                seenHostIds,
            );
        });

        if (pickHosts.length > 0) {
            return pickHosts;
        }

        collectImportedModelRenderableMeshTargets(sceneRoot).forEach((renderableTarget) => {
            this.collectPickHostsFromRenderableTarget(
                renderableTarget.object,
                renderableTarget.primitiveIndices,
                pickHosts,
                seenHostIds,
            );
        });

        return pickHosts;
    }

    /**
     * 从 renderable 目标收集 pick host。
     *
     * @param object 渲染目标对象。
     * @param primitiveIndices 局部 primitive 过滤。
     * @param pickHosts 输出 pick host 列表。
     * @param seenHostIds 已见 host UUID 集合。
     */
    private collectPickHostsFromRenderableTarget(
        object: THREE.Object3D,
        primitiveIndices: number[] | undefined,
        pickHosts: FeatureResultMesh[],
        seenHostIds: Set<string>,
    ): void {
        if (!(object instanceof THREE.Mesh)) {
            return;
        }

        const repository = resolveImportedModelTopologyRepository(object);
        if (!repository) {
            return;
        }

        const bodyRecords = this.resolveBodyRecordsForRenderableMesh(
            repository,
            object,
            primitiveIndices,
        );

        bodyRecords.forEach((bodyRecord) => {
            const pickHost = ensureImportedModelTopologyPickHost(bodyRecord);
            if (seenHostIds.has(pickHost.uuid)) {
                return;
            }
            seenHostIds.add(pickHost.uuid);
            pickHosts.push(pickHost);
        });
    }

    /**
     * 解析 highlightTargets 查找根节点。
     *
     * @param viewer Viewer 实例。
     * @param descriptor 设计树节点测量展开描述符。
     * @returns 查找根节点。
     */
    private resolveHighlightTargetSearchRoot(
        viewer: Viewer,
        descriptor: MeasureTreePartSelectionDescriptor,
    ): THREE.Object3D {
        const objectId = descriptor.objectId?.trim() ?? '';
        if (!objectId) {
            return viewer.scene;
        }

        return viewer.findSceneObjectById(objectId) ?? viewer.scene;
    }

    /**
     * 展开单个 pick host 下的全部可测量拓扑代理。
     *
     * @param pickHost 拓扑 pick host。
     * @returns 测量实体引用列表。
     */
    private expandPickHostMeasurableEntities(pickHost: FeatureResultMesh): MeasureEntityRef[] {
        const snapshot = pickHost.getTopologySnapshot();
        if (!snapshot) {
            return [];
        }

        const entities: MeasureEntityRef[] = [];

        snapshot.faces.forEach((face) => {
            const entityRef = this.toMeasureEntityRef(pickHost.attachFeatureFaceByIndex(face.index));
            if (entityRef) {
                entities.push(entityRef);
            }
        });

        snapshot.edges.forEach((edge) => {
            const entityRef = this.toMeasureEntityRef(pickHost.attachFeatureLineByIndex(edge.index));
            if (entityRef) {
                entities.push(entityRef);
            }
        });

        snapshot.vertices.forEach((vertex) => {
            const entityRef = this.toMeasureEntityRef(pickHost.attachFeaturePointByIndex(vertex.index));
            if (entityRef) {
                entities.push(entityRef);
            }
        });

        return entities;
    }

    /**
     * 将场景对象转换为测量实体引用。
     *
     * @param object 场景对象。
     * @returns 测量实体引用；不可测量时返回 null。
     */
    private toMeasureEntityRef(object: THREE.Object3D | null): MeasureEntityRef | null {
        if (!object) {
            return null;
        }

        return resolveMeasureEntityRefFromProgrammaticPick({
            distance: 0,
            object,
            point: object.getWorldPosition(new THREE.Vector3()),
        });
    }

    /**
     * 解析 renderable mesh 对应的 body 记录。
     *
     * @param repository 导入拓扑仓库。
     * @param mesh visible mesh。
     * @param primitiveIndices 局部 primitive 下标过滤。
     * @returns body 记录列表。
     */
    private resolveBodyRecordsForRenderableMesh(
        repository: ImportedModelTopologyRepository,
        mesh: THREE.Mesh,
        primitiveIndices?: number[],
    ): ImportedMeshTopologyBinding[] {
        if (!repository) {
            return [];
        }

        const allRecords = repository.resolveBodyRecordsByVisibleMesh(mesh);
        if (allRecords.length <= 0) {
            return [];
        }

        const normalizedPrimitiveIndices = this.normalizePrimitiveIndices(primitiveIndices);
        if (!normalizedPrimitiveIndices || normalizedPrimitiveIndices.length <= 0) {
            return allRecords;
        }

        const targetPrimitiveIds = new Set(
            this.resolvePrimitiveIdsFromLocalIndices(mesh, normalizedPrimitiveIndices),
        );
        if (targetPrimitiveIds.size <= 0) {
            return allRecords.length === 1 ? allRecords : [];
        }

        return allRecords.filter((record) => (
            record.bodyBinding.primitiveIds.some((primitiveId) => targetPrimitiveIds.has(primitiveId))
        ));
    }

    /**
     * 将 mesh 局部 primitive 下标映射为 stable primitiveId。
     *
     * @param mesh visible mesh。
     * @param localPrimitiveIndices 局部 primitive 下标。
     * @returns primitiveId 列表。
     */
    private resolvePrimitiveIdsFromLocalIndices(
        mesh: THREE.Mesh,
        localPrimitiveIndices: readonly number[],
    ): string[] {
        const primitiveFaceRanges = (
            mesh.userData?.partAssetLocal as { primitiveFaceRanges?: ImportedMeshPrimitiveFaceRangeItem[] } | undefined
        )?.primitiveFaceRanges;

        if (!Array.isArray(primitiveFaceRanges) || primitiveFaceRanges.length <= 0) {
            return [];
        }

        return localPrimitiveIndices
            .map((localIndex) => primitiveFaceRanges[localIndex]?.primitiveId)
            .filter((primitiveId): primitiveId is string => typeof primitiveId === 'string' && primitiveId.length > 0);
    }

    /**
     * 在子树中按 uuid 查找对象。
     *
     * @param root 查找根节点。
     * @param objectId 对象 uuid。
     * @returns 命中的对象。
     */
    private findObjectByIdInSubtree(root: THREE.Object3D, objectId: string): THREE.Object3D | null {
        if (root.uuid === objectId) {
            return root;
        }

        return root.getObjectByProperty('uuid', objectId) ?? null;
    }

    /**
     * 归一化 primitiveIndices。
     *
     * @param primitiveIndices primitive 下标集合。
     * @returns 清洗后的下标数组；无效时返回 undefined。
     */
    private normalizePrimitiveIndices(primitiveIndices?: number[]): number[] | undefined {
        if (!Array.isArray(primitiveIndices) || primitiveIndices.length <= 0) {
            return undefined;
        }

        const normalized = primitiveIndices.filter(
            (primitiveIndex) => Number.isInteger(primitiveIndex) && primitiveIndex >= 0,
        );

        return normalized.length > 0 ? normalized : undefined;
    }
}
